import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

export type RealtimeMessage = {
  type: string;
  data: Record<string, any>;
};

interface Options {
  /** Called on every non-ping message. Use it to refresh badge / list. */
  onMessage?: (msg: RealtimeMessage) => void;
  /** Reconnect backoff cap in ms (default 30 000) */
  maxBackoffMs?: number;
}

const _TOAST_LABELS: Record<string, (d: any) => string> = {
  'ticket.created': (d) => `Novo ticket: ${d.title ?? ''}`,
  'ticket.updated': (d) => `Ticket atualizado: ${d.title ?? ''} → ${d.new_status ?? ''}`,
  'credential.leaked': (d) => `Credenciais vazadas detectadas: ${d.domain ?? ''} (${d.count ?? 0})`,
  'event.created': (d) => `Novo evento: ${d.title ?? ''}`,
  'event.updated': (d) => `Evento atualizado: ${d.title ?? ''}`,
  'phishing.detected': (d) => `Phishing detectado: ${d.url ?? d.brand ?? ''}`,
  'threat.critical': (d) => `Ameaça crítica: ${d.title ?? ''}`,
  'asset.discovered': (d) => `Novo ativo descoberto: ${d.value ?? ''}`,
};

const _CRITICAL_TYPES = new Set(['credential.leaked', 'threat.critical', 'phishing.detected']);

function _buildWsUrl(token: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws/notifications?token=${token}`;
}

/**
 * Manages a WebSocket connection to /api/v1/ws/notifications.
 *
 * - Reconnects automatically with exponential backoff (1 s → 30 s cap).
 * - Shows a toast on each incoming notification.
 * - Calls `onMessage` so callers can refresh the badge / notification list.
 * - Disconnects cleanly on unmount or when token is removed.
 */
export function useRealtimeNotifications({ onMessage, maxBackoffMs = 30_000 }: Options = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1_000);
  const unmountedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || unmountedRef.current) return;

    const ws = new WebSocket(_buildWsUrl(token));
    wsRef.current = ws;

    ws.onopen = () => {
      backoffRef.current = 1_000; // reset backoff on successful connection
    };

    ws.onmessage = (event) => {
      let msg: RealtimeMessage;
      try {
        msg = JSON.parse(event.data) as RealtimeMessage;
      } catch {
        return;
      }
      if (msg.type === 'ping') return;

      const label = _TOAST_LABELS[msg.type]?.(msg.data ?? {}) ?? `Notificação: ${msg.type}`;
      if (_CRITICAL_TYPES.has(msg.type)) {
        toast.error(label, { duration: 8_000 });
      } else {
        toast(label, { duration: 5_000 });
      }

      onMessageRef.current?.(msg);
    };

    ws.onclose = (ev) => {
      // 4001 = auth failure — don't reconnect
      if (unmountedRef.current || ev.code === 4001) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, maxBackoffMs);
      setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [maxBackoffMs]);

  useEffect(() => {
    unmountedRef.current = false;
    connect();
    return () => {
      unmountedRef.current = true;
      wsRef.current?.close();
    };
  }, [connect]);
}
